import { fetcher } from "@website/fetcher/fetcher";
import { isInvalidCheckin, today, tomorrow } from "@website/utils/date-format";
import { isSSR } from "@website/utils/is-ssr";
import { replaceOptions } from "@website/utils/replace-options";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import get from "lodash-es/get";
import pick from "lodash-es/pick";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import type from "markdown";
import { GetServerSideProps } from "next";
import { filterXSS } from "xss";
import { ENDPOINTS } from "../../../constants";
import {
  HotelVillaRateDataType,
  HotelVillaReviewDataType,
  PersianVillaRatings,
  RoomDataType,
  RoomPropertyResponseType
} from "./index.types";

export const fetchRoomInfo = (_query: unknown) => {
  const query = isSSR() ? pick(_query, ["roomId"]) : _query;
  const parameters = {
    from: get(query, "date_from") || today(),
    to: get(query, "date_to") || tomorrow(),
    extraCapacity: get(query, "extra_capacity") || 0
  };
  return fetcher({
    url: ENDPOINTS.VILLA_GET_ROOM_INFO,
    options: { id: get(query, "roomId") },
    method: "GET",
    params: parameters,
    next: {
      revalidate: 300,
      tags: [
        ENDPOINTS.VILLA_GET_ROOM_INFO,
        get(query, "roomId"),
        JSON.stringify(parameters)
      ]
    }
  }).then(({ data }) => data as RoomDataType);
};

export const fetchHotelRateData = (hotelId: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_RATE,
    options: { hotelId },
    method: "GET",
    headers: {
      product: "villa"
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.HOTEL_RATE, toString(hotelId)]
    }
  }).then(({ data }) => {
    const _data = data as HotelVillaRateDataType;
    return {
      rateDetail: {
        clean_rate: _data?.cleaning || "",
        facility_rate: _data?.facilities || "",
        food_quality_rate: _data?.match || "",
        location_rate: _data?.location || "",
        staff_rate: _data?.friendly || "",
        value_for_money_rate: _data?.priceQuality || ""
      },
      rate: Number(_data?.overall).toFixed(1) ?? "",
      rateCount: _data?.rateCount ?? 0,
      offerCount: _data?.recommendationCount ?? 0,
      offerMessage:
        _data?.recommendationType === "recommendation"
          ? "۷۵ درصد از میهمانان اخیر، این اقامتگاه رو پیشنهاد کرده اند. "
          : _data?.recommendationType === "fullRate"
            ? "میهمانان در ۵ رزرو اخیر، امتیاز کامل را به اقامتگاه داده اند"
            : _data?.recommendationType
              ? `میهمانان در ۵ رزرو اخیر، به  "${PersianVillaRatings?.[
                  _data?.recommendationType
                ]}" امتیاز کامل داده اند.`
              : "",
      aiSummary: type?.markdown?.toHTML(data?.aiSummary)
    };
  });

export const fetchCalendarData = (roomId: unknown) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_CALENDAR, { roomId }),
    method: "GET",
    headers: {
      product: "villa"
    }
    // next: {
    //   revalidate: 60,
    //   tags: [ENDPOINTS.HOTEL_CALENDAR, toString(roomId)]
    // }
  })
    .then(({ data }) => data)
    .catch(() => ({
      roomID: roomId,
      inventoryTypes: null,
      from: today(),
      to: tomorrow(),
      calender: {}
    }));

export const fetchHotelReviewData = (
  hotelId: unknown,
  page: unknown,
  product: string,
  filters: Record<string, string>
) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW, { hotelId }),
    method: "GET",
    headers: {
      product
    },
    params: {
      ...filters,
      pageSize: 5,
      page
    }
  }).then(({ data }) => {
    const _data = data as HotelVillaReviewDataType;
    return {
      page: toNumber(_data?.page),
      total: _data?.total,
      rateReviews: (_data?.result ?? [])?.map((comment) => ({
        id: comment?.id,
        helpfulSubmitted: comment?.helpfulSubmitted,
        userName: comment?.passenger,
        roomName: comment.roomTitle,
        rate: String(comment?.avgRating || 0),
        registeredDate: comment?.createdAt,
        standNights: comment?.nights,
        comment:
          replaceSnappTripBasedOnChannel(
            filterXSS(comment?.comment, {
              whiteList: {},
              stripIgnoreTag: true,
              stripIgnoreTagBody: ["script"]
            })
          ) ?? "",
        strengths: comment?.pros?.filter(Boolean)?.join(" ,") ?? "",
        weakness: comment?.cons?.filter(Boolean)?.join(" ,") ?? "",
        pictures: comment?.images?.map((image, index) => ({
          id: index,
          media: `${process.env["NEXT_PUBLIC_BASE_URL_IMAGES"]}/reviews/${image}`,
          title: ""
        }))
      }))
    };
  });

export const fetchUsefulnessReviewData = (reviewId: number) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW_USEFULNESS, { reviewId }),
    method: "POST"
  }).then(() => {});

export const fetchPropertyPrice = (query: unknown) =>
  fetcher({
    url: ENDPOINTS.VILLA_ROOM_PRICE,
    options: { roomId: get(query, "roomId") },
    method: "GET",
    params: {
      from: get(query, "date_from")
        ? isInvalidCheckin(get(query, "date_from"), get(query, "date_to"))
          ? today()
          : get(query, "date_from")
        : today(),
      to: get(query, "date_to")
        ? isInvalidCheckin(get(query, "date_from"), get(query, "date_to"))
          ? tomorrow()
          : get(query, "date_to")
        : tomorrow(),
      extraCapacity: get(query, "extra_capacity") || 0
    }
  }).then(({ data }) => data as RoomPropertyResponseType);

export const fetchInstalmentWallet = (): Promise<{
  isEligible: boolean;
  showDownPayment: boolean;
  markup: number;
}> =>
  fetcher({
    url: ENDPOINTS.INSTALMENT_WALLET,
    method: "GET"
  }).then(({ data }) => ({
    isEligible: data?.is_eligible,
    showDownPayment: data?.show_down_payment,
    markup: data?.markup
  }));

export const getServerSideProps: GetServerSideProps = async ({
  query,
  res
}) => {
  const roomInfo = await fetchRoomInfo(query).catch(() => ({ error: true }));

  if ("error" in roomInfo || !roomInfo?.property?.id) {
    res.statusCode = 410;
    return { props: {} };
  }

  const [hotelRateData] = await Promise.all([
    fetchHotelRateData(roomInfo?.property?.id).catch(() => null)
  ]);

  return {
    props: {
      roomInfo,
      hotelRateData
    }
  };
};
